import type { ReactNode } from 'react';
import { Skeleton } from '@/components/States';

export type KpiTone = 'default' | 'positive' | 'negative' | 'muted';

const VALUE_TONE: Record<KpiTone, string> = {
  default: 'text-slate-900',
  positive: 'text-emerald-700',
  negative: 'text-rose-700',
  muted: 'text-slate-500',
};

export interface KpiTileProps {
  label: string;
  /** Already-formatted string — money keeps the API's exact digits. */
  value: string;
  hint?: string;
  tone?: KpiTone;
  icon?: ReactNode;
}

export function KpiTile({ label, value, hint, tone = 'default', icon }: KpiTileProps) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {icon ? <span className="shrink-0 text-slate-300">{icon}</span> : null}
      </div>
      <p className={`mt-2 break-words text-xl font-semibold tabular-nums ${VALUE_TONE[tone]}`}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

export function KpiTileSkeleton() {
  return (
    <div className="card p-4">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-6 w-28" />
      <Skeleton className="mt-2 h-2.5 w-16" />
    </div>
  );
}

/** Responsive tile grid: 1 column on phones, up to 4 on desktop. */
export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>;
}
